import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, AreaChart, Area, CartesianGrid,
} from "recharts";
import { Sparkles, Upload, Plug, ArrowRight } from "lucide-react";

// Friendly, "happy" palette — saturated but warm. Avoids the standard
// recharts purple-grey AI-slop. Each slice has a paired soft tint for
// gentle gradients on the funnel + donut.
const PALETTE = {
  claimed:    "#22A56A",   // emerald 500
  unclaimed:  "#F59E0B",   // amber 500
  pending:    "#6366F1",   // indigo 500
  pending2:   "#0EA5E9",   // sky 500
  rose:       "#EC4899",
  coral:      "#C05746",
  ink:        "#121614",
  muted:      "#5C625E",
  surface:    "#FDFBF7",
};

const STATUS_COLOR = {
  claimed:        PALETTE.claimed,
  unclaimed:      PALETTE.unclaimed,
  pending_email:  PALETTE.pending,
  pending_sms:    PALETTE.pending2,
  pending_review: PALETTE.rose,
};

const STATUS_LABEL = {
  claimed: "Claimed",
  unclaimed: "Unclaimed",
  pending_email: "Pending email",
  pending_sms: "Pending SMS",
  pending_review: "Pending review",
};

const FUNNEL_STEPS = [
  { key: "imported",        label: "Imported",        color: PALETTE.coral },
  { key: "claim_requested", label: "Claim requested", color: PALETTE.pending },
  { key: "claimed",         label: "Claimed",         color: PALETTE.claimed },
  { key: "has_menu",        label: "Menu published",  color: PALETTE.rose },
];

export default function ClaimDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/agency/claim-stats")
      .then(({ data }) => setStats(data))
      .finally(() => setLoading(false));
  }, []);

  const donut = useMemo(() => {
    if (!stats) return [];
    return (stats.by_status || []).map((s) => ({
      name: STATUS_LABEL[s.status] || s.status,
      value: s.count,
      color: STATUS_COLOR[s.status] || PALETTE.muted,
    })).filter((s) => s.value > 0);
  }, [stats]);

  const cuisineBars = useMemo(() => {
    if (!stats) return [];
    return (stats.by_cuisine || []).map((c) => ({
      label: c.label,
      Total: c.count,
      Claimed: c.claimed,
    }));
  }, [stats]);

  if (loading) {
    return (
      <div data-testid="claim-dashboard-loading" className="p-10 text-sm text-[#5C625E]">
        Loading claim insights…
      </div>
    );
  }
  if (!stats) {
    return (
      <div data-testid="claim-dashboard-error" className="p-10 text-sm text-red-600">
        Couldn&apos;t load stats. Check backend is running.
      </div>
    );
  }

  const t = stats.totals;

  return (
    <div
      data-testid="claim-dashboard"
      className="min-h-screen bg-gradient-to-br from-[#FFF7F0] via-[#FDFBF7] to-[#E6F5EE] p-4 sm:p-8"
    >
      <div className="max-w-7xl mx-auto space-y-8">
        {/* HEADER */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-3 py-1 text-xs font-medium text-[#5C625E] shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-[#F59E0B]" />
              Agency · Claim Insights
            </div>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#121614]">
              How your directory is converting
            </h1>
            <p className="mt-2 text-sm text-[#5C625E] max-w-2xl">
              Live snapshot of your bulk-imported restaurants — who&apos;s claimed,
              who&apos;s waiting, who&apos;s ready to sell. Refreshes every page load.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/agency/import"
              data-testid="dashboard-import-link"
              className="inline-flex items-center gap-2 rounded-full bg-[#121614] text-white px-4 py-2 text-sm font-medium hover:opacity-90"
            >
              <Upload className="h-4 w-4" />
              Bulk import
            </Link>
            <Link
              to="/admin/integrations"
              data-testid="dashboard-integrations-link"
              className="inline-flex items-center gap-2 rounded-full border border-[#121614]/15 bg-white px-4 py-2 text-sm font-medium hover:bg-black/5"
            >
              <Plug className="h-4 w-4" />
              Integrations
            </Link>
          </div>
        </header>

        {/* TOP ROW — KPI cards + analogue gauge */}
        <section className="grid lg:grid-cols-3 gap-4">
          <KpiCard label="Total listings"  value={t.total}     tint={PALETTE.coral}    testid="kpi-total" />
          <KpiCard label="Claimed"         value={t.claimed}   tint={PALETTE.claimed}  testid="kpi-claimed" />
          <KpiCard label="Unclaimed"       value={t.unclaimed} tint={PALETTE.unclaimed} testid="kpi-unclaimed" />
        </section>

        {/* MIDDLE — analogue gauge + donut */}
        <section className="grid lg:grid-cols-5 gap-4">
          <div
            data-testid="claim-rate-gauge-card"
            className="lg:col-span-2 rounded-3xl bg-white shadow-sm border border-black/5 p-6 flex flex-col items-center"
          >
            <h2 className="text-sm uppercase tracking-wide text-[#5C625E] font-medium">
              Claim rate
            </h2>
            <AnalogueGauge percent={t.claim_rate_pct} />
            <p className="mt-2 text-xs text-[#5C625E] text-center max-w-xs">
              Share of your directory that has been claimed by the venue owner.
              Tick the needle past 60% and the platform basically runs itself.
            </p>
          </div>
          <div
            data-testid="claim-status-donut-card"
            className="lg:col-span-3 rounded-3xl bg-white shadow-sm border border-black/5 p-6"
          >
            <h2 className="text-sm uppercase tracking-wide text-[#5C625E] font-medium">
              Status breakdown
            </h2>
            <div className="grid sm:grid-cols-2 items-center gap-4 mt-2">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donut}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={88}
                      paddingAngle={2}
                      stroke="#FFFFFF"
                      strokeWidth={3}
                    >
                      {donut.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(0,0,0,.12)" }}
                      formatter={(v, n) => [`${v} listings`, n]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-2">
                {donut.map((d) => (
                  <li
                    key={d.name}
                    data-testid={`donut-legend-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="flex items-center gap-2 text-sm text-[#121614]">
                      <span
                        className="inline-block h-3 w-3 rounded-full"
                        style={{ backgroundColor: d.color }}
                      />
                      {d.name}
                    </span>
                    <span className="text-sm font-medium tabular-nums">{d.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FUNNEL */}
        <section
          data-testid="claim-funnel"
          className="rounded-3xl bg-white shadow-sm border border-black/5 p-6"
        >
          <h2 className="text-sm uppercase tracking-wide text-[#5C625E] font-medium">
            Conversion funnel
          </h2>
          <p className="mt-1 text-xs text-[#5C625E]">
            Each step is the subset of listings that made it to the next stage.
          </p>
          <div className="mt-5 grid sm:grid-cols-4 gap-3">
            {FUNNEL_STEPS.map((step, i) => {
              const count = stats.funnel[step.key] || 0;
              const baseline = stats.funnel.imported || 1;
              const pct = Math.round((count / baseline) * 100);
              return (
                <div
                  key={step.key}
                  data-testid={`funnel-${step.key}`}
                  className="relative rounded-2xl overflow-hidden border border-black/5"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}18, ${step.color}05)`,
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-[#5C625E]">
                      <span className="h-5 w-5 rounded-full grid place-items-center text-[10px] font-bold text-white"
                            style={{ backgroundColor: step.color }}>
                        {i + 1}
                      </span>
                      {step.label}
                    </div>
                    <p className="mt-3 font-serif-display text-3xl tabular-nums text-[#121614]">
                      {count}
                    </p>
                    <p className="mt-0.5 text-xs text-[#5C625E]">{pct}% of imports</p>
                  </div>
                  <div
                    className="h-1.5 transition-all"
                    style={{ width: `${pct}%`, backgroundColor: step.color }}
                  />
                </div>
              );
            })}
          </div>
        </section>

        {/* BOTTOM ROW — cuisines bar + recent claims spark */}
        <section className="grid lg:grid-cols-5 gap-4">
          <div
            data-testid="cuisine-bar-card"
            className="lg:col-span-3 rounded-3xl bg-white shadow-sm border border-black/5 p-6"
          >
            <h2 className="text-sm uppercase tracking-wide text-[#5C625E] font-medium">
              Top venue categories
            </h2>
            <div className="h-72 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cuisineBars} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="#0001" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: PALETTE.muted }} interval={0} angle={-18} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: PALETTE.muted }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(0,0,0,.12)" }}
                    cursor={{ fill: "#0001" }}
                  />
                  <Bar dataKey="Total" radius={[6, 6, 0, 0]} fill={PALETTE.coral} />
                  <Bar dataKey="Claimed" radius={[6, 6, 0, 0]} fill={PALETTE.claimed} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            data-testid="recent-claims-card"
            className="lg:col-span-2 rounded-3xl bg-white shadow-sm border border-black/5 p-6"
          >
            <h2 className="text-sm uppercase tracking-wide text-[#5C625E] font-medium">
              Claims · last 30 days
            </h2>
            <div className="h-56 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.recent} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="recentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"  stopColor={PALETTE.claimed} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={PALETTE.claimed} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#0001" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: PALETTE.muted }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: PALETTE.muted }} allowDecimals={false} width={24} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(0,0,0,.12)" }}
                    formatter={(v) => [`${v} claims`, "Claimed"]}
                  />
                  <Area type="monotone" dataKey="claimed" stroke={PALETTE.claimed} strokeWidth={2.5} fill="url(#recentGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <Link
              to="/agency"
              data-testid="dashboard-tenants-link"
              className="mt-3 inline-flex items-center gap-1 text-sm text-[#C05746] hover:underline"
            >
              Manage tenants <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}


function KpiCard({ label, value, tint, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden rounded-3xl bg-white shadow-sm border border-black/5 p-6"
    >
      <div
        aria-hidden
        className="absolute -top-12 -right-12 h-40 w-40 rounded-full opacity-15"
        style={{ backgroundColor: tint }}
      />
      <p className="relative text-sm uppercase tracking-wide text-[#5C625E] font-medium">
        {label}
      </p>
      <p
        className="relative mt-2 font-serif-display text-5xl tabular-nums"
        style={{ color: tint }}
      >
        {value}
      </p>
    </div>
  );
}


/**
 * SVG analogue gauge — speedometer with coloured arc, tick marks,
 * and a sweeping needle. Animates from 0 → percent on mount via CSS.
 */
function AnalogueGauge({ percent = 0 }) {
  const p = Math.max(0, Math.min(100, percent));
  // Arc spans -120° to +120° (240° total). Convert percent → angle.
  const startAngle = -120;
  const endAngle = 120;
  const angle = startAngle + (p / 100) * (endAngle - startAngle);
  const cx = 110;
  const cy = 110;
  const r = 80;
  const polar = (a) => {
    const rad = (a - 90) * (Math.PI / 180);
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };

  // Coloured arc segments (3 zones): red 0-40, amber 40-70, green 70-100
  const arcSeg = (a1, a2, color) => {
    const [x1, y1] = polar(a1);
    const [x2, y2] = polar(a2);
    const large = a2 - a1 > 180 ? 1 : 0;
    return (
      <path
        d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`}
        stroke={color} strokeWidth={14} fill="none" strokeLinecap="round"
      />
    );
  };

  // Needle endpoint
  const [nx, ny] = polar(angle);

  // Tick marks every 10%
  const ticks = [];
  for (let i = 0; i <= 10; i++) {
    const a = startAngle + (i / 10) * 240;
    const [tx1, ty1] = polar(a);
    const inner = r - 8;
    const [tx2, ty2] = (() => {
      const rad = (a - 90) * (Math.PI / 180);
      return [cx + inner * Math.cos(rad), cy + inner * Math.sin(rad)];
    })();
    ticks.push(
      <line
        key={i}
        x1={tx1} y1={ty1} x2={tx2} y2={ty2}
        stroke={i % 5 === 0 ? "#121614" : "#5C625E66"}
        strokeWidth={i % 5 === 0 ? 2 : 1}
        strokeLinecap="round"
      />,
    );
  }

  return (
    <div data-testid="claim-rate-gauge" className="relative w-full max-w-xs aspect-[1.05]">
      <svg viewBox="0 0 220 200" className="w-full h-auto">
        {/* Background track */}
        <path
          d={`M ${polar(startAngle).join(" ")} A ${r} ${r} 0 1 1 ${polar(endAngle).join(" ")}`}
          stroke="#EFEAE0" strokeWidth={14} fill="none" strokeLinecap="round"
        />
        {/* Coloured zones */}
        {arcSeg(startAngle,        startAngle + 96,   PALETTE.unclaimed)}{/* 0-40 amber */}
        {arcSeg(startAngle + 96,   startAngle + 168,  PALETTE.pending2)}{/* 40-70 sky  */}
        {arcSeg(startAngle + 168,  endAngle,          PALETTE.claimed)}{/* 70-100 green */}
        {ticks}
        {/* Needle */}
        <g
          style={{
            transformOrigin: `${cx}px ${cy}px`,
            transform: `rotate(${angle - (angle)}deg)`,
          }}
        >
          <line
            x1={cx} y1={cy} x2={nx} y2={ny}
            stroke={PALETTE.ink} strokeWidth={3.5} strokeLinecap="round"
            style={{
              transformOrigin: `${cx}px ${cy}px`,
              animation: "gauge-sweep 1.2s ease-out",
            }}
          />
          <circle cx={cx} cy={cy} r={8} fill={PALETTE.coral} stroke="#fff" strokeWidth={2} />
        </g>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2 pointer-events-none">
        <span
          data-testid="claim-rate-value"
          className="font-serif-display text-5xl tabular-nums"
          style={{ color: PALETTE.ink }}
        >
          {p.toFixed(1)}<span className="text-2xl text-[#5C625E]">%</span>
        </span>
      </div>
      {/* keyframes for needle sweep — scoped via inline <style> so we
          don't have to touch global CSS. */}
      <style>{`
        @keyframes gauge-sweep {
          0%   { transform: rotate(-120deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
}
